import { Dashboard_mode } from '../../constants';
import { Data } from '../Data';

export type TranslatableText = string | { key: string; params?: Record<string, string> };

export class Notification {
  static type = {
    added: {
      index: 0,
      labelKey: 'notifications.types.competencyAdded',
    },
    edited: {
      index: 1,
      labelKey: 'notifications.types.competencyEdited',
    },
    changeRequest: {
      index: 2,
      labelKey: 'notifications.types.competencyChangeRequest',
    },
    changeResponse: {
      index: 3,
      labelKey: 'notifications.types.competencyChangeResponse',
    },
    approved: {
      index: 4,
      labelKey: 'notifications.types.competencyApproved',
    },
    aboutToExpire: {
      index: 5,
      labelKey: 'notifications.types.competencyAboutToExpire',
    },
    demoOrgResetting: {
      index: 6,
      labelKey: 'notifications.types.demoOrgResetting',
    },
    demoOrgResetDone: {
      index: 7,
      labelKey: 'notifications.types.demoOrgResetDone',
    },
    employeeAddedAsGroupManager: {
      index: 8,
      labelKey: '',
    },
    attributeExpired: {
      index: 9,
      labelKey: 'notifications.types.competencyExpired',
    },
    participateAcknowedgementRequest: {
      index: 10,
      labelKey: 'notifications.types.participateAcknowledgementRequest',
    },
    participantAcknowledged: {
      index: 11,
      labelKey: 'notifications.types.participantAcknowledged',
    },
    courseEnrolmentRequested: {
      index: 12,
      labelKey: 'notifications.types.courseEnrolmentRequested',
    },
    courseEnrolmentApproved: {
      index: 13,
      labelKey: 'notifications.types.courseEnrolmentApproved',
    },
    courseEnrolmentDeclined: {
      index: 14,
      labelKey: 'notifications.types.courseEnrolmentDeclined',
    },
    contentModerationRejected: {
      index: 15,
      labelKey: 'notifications.types.contentModerationRejected',
    },
  };

  static state = {
    unread: {
      index: 0,
      labelKey: 'notifications.states.unread',
    },
    read: {
      index: 1,
      labelKey: 'notifications.states.read',
    },
    dismissed: {
      index: 2,
      labelKey: 'notifications.states.dismissed',
    },
  };

  /**
   * Resolves a translatable text value using the provided translate function.
   * If the text is a string, it returns it as-is.
   * If the text is an object with a key, it calls translate with the key and params.
   */
  static resolveText(
    text: TranslatableText | null | undefined,
    translate: (key: string, options?: Record<string, unknown>) => string
  ): string {
    if (!text) return '';
    if (typeof text === 'string') return text;
    return translate(text.key, text.params);
  }

  id: string;
  isNew: boolean;
  notificationId: string;
  dashboardMode: any;
  date: string;
  type: any;
  state: any;
  primaryText!: TranslatableText;
  secondaryText!: TranslatableText;
  practicalId?: string;
  practicalParticipantId?: string;
  employeeAttributeId?: string;
  enrolmentRequestId?: string;
  courseId?: string;
  employeeId?: string;
  groupId?: string;
  alsoInOtherGroupIds?: string[];
  showOnceId: string;

  constructor(data: any, dashboardMode: any) {
    this.id = data.notificationId;

    this.isNew = false;
    this.notificationId = Data.getFromData(data, 'notificationId');
    this.id = this.notificationId;
    this.dashboardMode = dashboardMode;
    this.date = Data.getFromData(data, 'createDate');
    this.type = Notification.getTypeFromIndex(Data.getFromData(data, 'notificationType'));
    this.state = Notification.getStateFromIndex(Data.getFromData(data, 'notificationState'));

    //Personal
    if (dashboardMode === Dashboard_mode.personal) {
      if (this.type === Notification.type.participateAcknowedgementRequest) {
        this.primaryText = {
          key: 'notifications.practical',
          params: { name: Data.getFromData(data, 'practicalName') },
        };
        this.secondaryText = { key: this.type.labelKey };
        this.practicalId = Data.getFromData(data, 'practicalId');
        this.practicalParticipantId = Data.getFromData(data, 'practicalParticipantId');
      } else if (this.type === Notification.type.participantAcknowledged) {
        this.primaryText = {
          key: 'notifications.practicalAtTime',
          params: {
            name: Data.getFromData(data, 'practicalName'),
            time: Data.getFromData(data, 'PracticalDateTime'),
          },
        };
        const participantName = Data.getFromData(data, 'participantFullName');
        this.secondaryText = {
          key: 'notifications.confirmedParticipation',
          params: { name: participantName },
        };
        this.practicalId = Data.getFromData(data, 'practicalId');
      } else if (Notification.isEnrolmentRequestType(this.type)) {
        this.primaryText = Data.getFromData(data, 'courseName');
        this.secondaryText =
          this.type === Notification.type.courseEnrolmentRequested
            ? {
                key: 'notifications.enrolmentRequestedBy',
                params: { name: Data.getFromData(data, 'requesterFullName') },
              }
            : { key: this.type.labelKey };
        this.enrolmentRequestId = Data.getFromData(data, 'enrolmentRequestId');
        this.courseId = Data.getFromData(data, 'courseId');
      } else {
        this.primaryText = Data.getFromData(data, 'attributeDefinitionName');
        this.secondaryText = { key: this.type.labelKey };
        this.employeeAttributeId = Data.getFromData(data, 'employeeAttributeId');
      }
    }

    //Team
    else if (dashboardMode === Dashboard_mode.team) {
      this.primaryText =
        Data.getFromData(data, 'employeeAttributeValidationRequestEmployeeFirstName') +
        ' ' +
        Data.getFromData(data, 'employeeAttributeValidationRequestEmployeeLastName');
      this.secondaryText = this.type ? { key: this.type.labelKey } : '';
      this.employeeId = Data.getFromData(data, 'employeeId');
      this.employeeAttributeId = Data.getFromData(data, 'EmployeeAttributeId');
    }

    //Groups
    else if (dashboardMode === Dashboard_mode.groups) {
      this.primaryText =
        Data.getFromData(data, 'employeeAttributeValidationRequestEmployeeFirstName') +
        ' ' +
        Data.getFromData(data, 'employeeAttributeValidationRequestEmployeeLastName');
      this.secondaryText = this.type ? { key: this.type.labelKey } : '';
      this.employeeId = Data.getFromData(data, 'employeeId');
      this.employeeAttributeId = Data.getFromData(data, 'EmployeeAttributeId');
      this.groupId = Data.getFromData(data, 'groupId');
      this.alsoInOtherGroupIds = Data.getFromData(data, 'alsoInOtherGroupIds');
    }

    this.showOnceId =
      this.employeeId +
      '_' +
      (this.employeeAttributeId ?? this.enrolmentRequestId) +
      '_' +
      this.type.index;
  }

  static isEnrolmentRequestType(type: any) {
    return (
      type === Notification.type.courseEnrolmentRequested ||
      type === Notification.type.courseEnrolmentApproved ||
      type === Notification.type.courseEnrolmentDeclined
    );
  }

  isValid() {
    const hasPrimaryText =
      this.primaryText &&
      (typeof this.primaryText === 'string'
        ? this.primaryText.length > 0
        : this.primaryText.key && this.primaryText.key.length > 0);
    const hasSecondaryText =
      this.secondaryText &&
      (typeof this.secondaryText === 'string'
        ? this.secondaryText.length > 0
        : this.secondaryText.key && this.secondaryText.key.length > 0);
    return hasPrimaryText && hasSecondaryText;
  }

  isState(state: any) {
    return this.state === state;
  }

  isRead() {
    return this.isState(Notification.state.read);
  }

  setAsRead() {
    return (this.state = Notification.state.read);
  }

  isMode(dashboardMode: any) {
    return this.dashboardMode === dashboardMode;
  }

  static getTypeFromIndex(index: any) {
    return Object.values(Notification.type)[index];
  }

  static getStateFromIndex(index: any) {
    return Object.values(Notification.state)[index];
  }
}
